import { pulseAxiosInstance } from "@pulse/api/axiosInstance";

export const createSurvey = async ({
    pulseInstance,
    data,
}: {
    pulseInstance: number;
    data: CreateSurvey;
}): Promise<Survey> =>
    (await pulseAxiosInstance.post(`surveys/pulse-instance/${pulseInstance}`, data)).data;
export const updateSurvey = async ({
    surveyId,
    data,
}: {
    surveyId: number;
    data: Partial<CreateSurvey>;
}): Promise<Survey> => (await pulseAxiosInstance.patch(`surveys/${surveyId}`, data)).data;

export const deleteSurvey = async ({ surveyId }: { surveyId: number }): Promise<Survey> =>
    (await pulseAxiosInstance.delete(`surveys/${surveyId}`)).data;

export const softDeleteSurvey = async ({ surveyId }: { surveyId: number }): Promise<Survey> =>
    (await pulseAxiosInstance.delete(`surveys/soft/${surveyId}`)).data;

export const getSurveyById = async ({ surveyId }: { surveyId: number }): Promise<Survey> =>
    (await pulseAxiosInstance.get(`surveys/${surveyId}`)).data;

export const getSurveyForParticipant = async ({
    surveyId,
}: {
    surveyId: number;
}): Promise<Survey> => (await pulseAxiosInstance.get(`surveys/${surveyId}/participant`)).data;

export const getQuestionsForParticipants = async ({
    surveyId,
}: {
    surveyId: number;
}): Promise<Question[]> =>
    (await pulseAxiosInstance.get(`questions/survey/${surveyId}/participant`)).data;

export const getQuestionsForManagers = async ({
    surveyId,
}: {
    surveyId: number;
}): Promise<Question[]> =>
    (await pulseAxiosInstance.get(`questions/survey/${surveyId}/manager`)).data;

export const createQuestion = async ({
    surveyId,
    data,
}: {
    surveyId: number;
    data: CreateQuestion;
}): Promise<Question> =>
    (await pulseAxiosInstance.post(`/questions/survey/${surveyId}`, data)).data;

export const completeSurvey = async ({ surveyId }: { surveyId: number }): Promise<Survey> =>
    (await pulseAxiosInstance.patch(`surveys/complete/${surveyId}`)).data;

export const deleteQuestion = async ({ questionId }: { questionId: number }): Promise<Question> =>
    (await pulseAxiosInstance.delete(`/questions/${questionId}`)).data;

export const updateQuestion = async ({
    questionId,
    data,
}: {
    questionId: number;
    data: Partial<CreateQuestion>;
}): Promise<Question> => (await pulseAxiosInstance.patch(`/questions/${questionId}`, data)).data;

export const reorderQuestions = async ({
    surveyId,
    questionIds,
}: {
    surveyId: number;
    questionIds: number[];
}): Promise<Question[]> =>
    (await pulseAxiosInstance.patch(`/questions/survey/${surveyId}/reorder`, { questionIds })).data;

export const getAllSurveys = async ({
    pulseInstanceId,
}: {
    pulseInstanceId: number;
}): Promise<Survey[]> =>
    (await pulseAxiosInstance.get(`surveys/pulse-instance/${pulseInstanceId}`)).data;
export const submitSurvey = async ({
    surveyId,
    data,
}: {
    surveyId: number;
    data: Answer[];
}): Promise<Survey> =>
    (await pulseAxiosInstance.post(`answers/survey/answer`, { surveyId, answers: data })).data;

export type SurveyStatus = "DRAFT" | "ACTIVE" | "COMPLETED" | "EXPIRED" | "STOPPED";

export type SurveyCreator = {
    firstName: string;
    lastName: string;
};

export const QuestionType = {
    TEXT: "TEXT",
    RATING: "RATING",
    MULTIPLE_CHOICE: "MULTIPLE_CHOICE",
    MULTI_SELECT: "MULTI_SELECT",
} as const;

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

type BaseQuestion = {
    id: number;
    surveyId: number;
    questionText: string;
    questionOrder: number;
    allowComments?: boolean;
    optional: boolean;
    driverId: number | null;
    createdAt: Date;
    updatedAt: Date;
    createdById: number;
    updatedById: number;
};

type BaseTextQuestion = {
    questionType: typeof QuestionType.TEXT;
    questionOptions?: null;
};

type BaseRatingQuestion = {
    questionType: typeof QuestionType.RATING;
    questionOptions?: null;
};

type BaseMultipleChoiceQuestion = {
    questionType: typeof QuestionType.MULTIPLE_CHOICE;
    questionOptions: string[];
};

type BaseMultiSelectQuestion = {
    questionType: typeof QuestionType.MULTI_SELECT;
    questionOptions: string[];
};

type QuestionTypeVariant =
    | BaseTextQuestion
    | BaseRatingQuestion
    | BaseMultipleChoiceQuestion
    | BaseMultiSelectQuestion;

export type Question = BaseQuestion & QuestionTypeVariant;

export type Survey = {
    status: SurveyStatus;
    title: string;
    description: string | null;
    pulseInstanceId: number;
    id: number;
    globalId: string;
    createdAt: Date;
    updatedAt: Date;
    deletedAt: Date | null;
    startedAt: Date | null;
    reminderSentAt: Date | null;
    createdById: number;
    createdBy?: SurveyCreator;
    updatedById: number;
};

export type CreateSurvey = {
    title: string;
    description?: string | null;
};

type BaseCreateQuestion = {
    questionText: string;
    questionOrder: number;
    driverId?: number | null;
    questionType: QuestionType;
    questionOptions?: string[] | null;
    allowComments?: boolean;
    optional?: boolean;
};

export type PartialCreateQuestion = Partial<BaseCreateQuestion> & QuestionTypeVariant;

export type CreateQuestion = BaseCreateQuestion & QuestionTypeVariant;

type AnswerValueMap = {
    [QuestionType.TEXT]: string;
    [QuestionType.RATING]: number;
    [QuestionType.MULTIPLE_CHOICE]: number;
    [QuestionType.MULTI_SELECT]: number[];
};

export type Answer<TQuestionType extends QuestionType = QuestionType> = {
    surveyQuestionId: number;
    answerValue?: AnswerValueMap[TQuestionType];
    answerComment?: string;
};
